import React from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { FileText, Plus } from 'lucide-react';
import type { TemplateFilterOption } from '../types';

interface TemplateEmptyStateProps {
  searchTerm: string;
  filterType: TemplateFilterOption;
  isSeeding: boolean;
  onClearFilters: () => void;
  onCreateClick: () => void;
  onLoadSamplesClick: () => void;
}

export const TemplateEmptyState: React.FC<TemplateEmptyStateProps> = ({
  searchTerm,
  filterType,
  isSeeding,
  onClearFilters,
  onCreateClick,
  onLoadSamplesClick,
}) => {
  const isFiltered = Boolean(searchTerm) || filterType !== 'all';

  return (
    <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-[16px] max-w-md mx-auto p-8 shadow-sm">
      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4 stroke-[1.5]" />
      <h3 className="text-lg font-bold text-slate-900 mb-2">
        {isFiltered ? 'No matching templates' : 'No templates found'}
      </h3>
      <p className="text-slate-500 text-sm mb-6 leading-relaxed">
        {isFiltered
          ? 'Try adjusting your search terms or filters'
          : 'Create your first email template to get started, or load the design sample templates.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {isFiltered ? (
          <Button
            variant="outline"
            className="rounded-xl border-slate-200"
            onClick={onClearFilters}
          >
            Clear Filters
          </Button>
        ) : (
          <>
            <Button
              onClick={onCreateClick}
              className="bg-[#A655F8] hover:bg-[#9333EA] text-white font-medium rounded-xl px-5"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
            <Button
              onClick={onLoadSamplesClick}
              disabled={isSeeding}
              variant="outline"
              className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
            >
              {isSeeding ? (
                <LoadingSpinner size="small" iconClassName="text-[#A655F8]" label="Loading..." />
              ) : (
                'Load Sample Templates'
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
